import { ActivatedRoute, Params } from '@angular/router';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import { ITenant } from '../../../../../shared/interfaces/tenant.interface';
import { TenantService } from '../../../services/tenant.service';
import { $tenant } from '../../../../../shared/constants/tenant';
import { Api } from '../../../../../shared/constants/api';
import { AngularUtility } from '../../../utility/angular.utility';
import { $tenantDetailsTabs } from '../../../constants/tenant.constants';
import { $academy } from '../../../../../shared/constants/academy';
import { SharedCommonUtility } from '../../../../../shared/utils/common.utility';
import { IRequiredSecurity } from '../../../../../shared/interfaces/security.interface';
import { RequiredSecurities } from '../../../../../shared/constants/required-securities';
import { UserAclService } from '../../../services/user-acl.service';
import { AclSecurityAdapter } from '../../../services/acl.service';
import { FeatureFlagService } from '../../../services/feature-flag/feature-flag.service';
import { FeatureFlagCollection } from '../../../../../shared/interfaces/feature-flag.interface';
import { AppConfigService } from '../../../services/app-config.service';

@Component({
  selector: 'app-tenant-details',
  templateUrl: 'tenant-details.component.html',
  styleUrls: ['tenant-details.component.scss'],
  standalone: false,
})
export class TenantDetailsComponent implements OnInit, OnDestroy {
  private subscription: Subscription;

  public tenantId$: Observable<string>;
  public tenant$: Observable<ITenant>;
  public $tenant: typeof $tenant;
  public tenant: ITenant;
  public Api: typeof Api;
  public activeTab$: Observable<string>;
  public hasAccessToAcademy$: Observable<boolean>;
  public showInternalNotesHideDescription$: Observable<boolean>;
  public readonly isAdminView: boolean;
  public defaultTab: $tenantDetailsTabs;
  public $tenantDetailsTabs: typeof $tenantDetailsTabs = $tenantDetailsTabs;

  public updateTenantRequiredSecurity: IRequiredSecurity;
  public readWorkspaceRequiredSecurity: IRequiredSecurity;

  public constructor(
    private activatedRoute: ActivatedRoute,
    private tenantService: TenantService,
    private userAclService: UserAclService,
    private featureFlagService: FeatureFlagService,
    private appConfigService: AppConfigService,
  ) {
    this.isAdminView = this.appConfigService.isTenantEnabled() === false;
    this.$tenant = $tenant;
    this.Api = Api;
    this.subscription = new Subscription();

    this.updateTenantRequiredSecurity = RequiredSecurities.Tenants_Update;
    this.readWorkspaceRequiredSecurity = RequiredSecurities.Workspaces_Read;
  }

  private isValidTab(value: string): boolean {
    return Object.values($tenantDetailsTabs).includes(value as any);
  }

  private buildFragmentChangesObservable(): void {
    this.activeTab$ = this.activatedRoute.fragment.pipe(
      map((fragment: string) => (this.isValidTab(fragment) ? fragment : this.defaultTab)),
    );
  }

  public get academyDisabled(): boolean {
    return (
      SharedCommonUtility.isNullishOrEmpty(this.tenant?.[$tenant.academyConfig]?.[$academy.academyId]) &&
      SharedCommonUtility.isNullishOrEmpty(this.tenant?.[$tenant.academyConfig]?.[$academy.externalPackageSignupField])
    );
  }

  public ngOnInit(): void {
    this.tenantId$ = this.activatedRoute.params.pipe(map((params: Params) => params[Api.tenantId]));

    this.tenant$ = this.tenantId$.pipe(
      switchMap((tenantId: string): Observable<ITenant> => this.tenantService.getTenant(tenantId)),
      AngularUtility.shareRef<ITenant>(),
    );

    this.subscription.add(
      this.tenant$.subscribe((tenant: ITenant) => {
        this.tenant = tenant;
      }),
    );

    this.defaultTab = $tenantDetailsTabs.workspaces;
    this.activeTab$ = new Observable<string>();

    this.hasAccessToAcademy$ = this.userAclService
      .createCheckAccessForCurrentUser()
      .pipe(
        map((adapter: AclSecurityAdapter): boolean =>
          adapter.useFunctionalActions(RequiredSecurities.Academy_Read.functionalActions).check(),
        ),
      );

    this.showInternalNotesHideDescription$ = this.featureFlagService
      .variation$(FeatureFlagCollection.showInternalNotesHideDescription, false)
      .pipe(map(Boolean));

    this.buildFragmentChangesObservable();
  }

  public ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
